'use client';
import React, { createContext, PropsWithChildren, useContext } from 'react';
import { useQuery } from 'react-query';
import { useFetch } from './FetchProvider';
import { fetchTools } from '../constants';
import { useUserContext } from './UserContext';
import { ProductComponent } from '@osmosis/shared/products';

type UserToolsContextProps = {
  tools: ProductComponent[];
  userHasTool: (toolName: string) => boolean;
};

const UserToolsContext = createContext<UserToolsContextProps>({
  tools: [],
  userHasTool: () => false,
});

/**
 * UserTools provider.
 */
export const UserToolsProvider = ({ children }: PropsWithChildren) => {
  const { get } = useFetch();
  const { user } = useUserContext();

  const { data: tools = [] } = useQuery(
    ['user-tools', user],
    async (): Promise<ProductComponent[]> => {
      if (!user) {
        return [];
      }
      return fetchTools(get)();
    },
    {
      // These tools won't change during a session, so we can set an infinite staleTime
      staleTime: Infinity,
    },
  );

  const userHasTool = (toolName: string) => {
    return !!tools.find((tool) => tool.type === toolName);
  };

  return (
    <UserToolsContext.Provider
      value={{
        tools,
        userHasTool,
      }}
    >
      {children}
    </UserToolsContext.Provider>
  );
};

/**
 * Hook to access the user tools context.
 */
export const useUserTools = (): UserToolsContextProps =>
  useContext(UserToolsContext);
